import React, {useState, useMemo} from 'react';
import {Modal, Form, Alert, ProgressBar} from 'react-bootstrap';
import {IconEye, IconEyeOff} from '@tabler/icons-react';
import {apiService} from '../services/api';
import {storage} from '../utils/localStorage';

declare global {
    interface Window {
        PasswordCredential?: new (data: { id: string; password: string }) => Credential;
    }
}

interface ChangePasswordModalProps {
    show: boolean;
    onHide: () => void;
    userEmail?: string;
}

interface PasswordStrength {
    score: number;
    label: string;
    variant: string;
}

function getPasswordStrength(password: string): PasswordStrength {
    if (!password) return {score: 0, label: '', variant: 'secondary'};

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 1) return {score: 20, label: 'Veľmi slabé', variant: 'danger'};
    if (score === 2) return {score: 40, label: 'Slabé', variant: 'warning'};
    if (score === 3) return {score: 60, label: 'Stredné', variant: 'info'};
    if (score === 4) return {score: 80, label: 'Silné', variant: 'primary'};
    return {score: 100, label: 'Veľmi silné', variant: 'success'};
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({show, onHide, userEmail}) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
        setError(null);
        setSuccess(false);
        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
        onHide();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword.length < 8) {
            setError('Nové heslo musí mať aspoň 8 znakov.');
            return;
        }

        if (newPassword !== newPasswordConfirm) {
            setError('Nové heslá sa nezhodujú.');
            return;
        }

        setLoading(true);

        const response = await apiService.changePassword(currentPassword, newPassword);

        if (response.success) {
            setSuccess(true);
            // Update saved password for auto re-login on 401
            storage.setItem('savedPassword', newPassword);
            // Trigger browser password save prompt
            if (userEmail) {
                if (window.PasswordCredential) {
                    try {
                        const credential = new window.PasswordCredential({
                            id: userEmail,
                            password: newPassword,
                        });
                        await navigator.credentials.store(credential);
                    } catch (_) {
                        // Ignore - not critical
                    }
                } else {
                    // Fallback for Safari - off-screen form (Safari ignores display:none)
                    const offscreen = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';

                    const iframe = document.createElement('iframe');
                    iframe.name = 'pw-save-frame';
                    iframe.setAttribute('style', offscreen + 'width:0;height:0;border:0;');
                    document.body.appendChild(iframe);

                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = 'about:blank';
                    form.target = 'pw-save-frame';
                    form.setAttribute('style', offscreen);

                    const emailInput = document.createElement('input');
                    emailInput.type = 'text';
                    emailInput.name = 'username';
                    emailInput.autocomplete = 'username';
                    emailInput.value = userEmail;

                    const pwInput = document.createElement('input');
                    pwInput.type = 'password';
                    pwInput.name = 'password';
                    pwInput.autocomplete = 'current-password';
                    pwInput.value = newPassword;

                    form.appendChild(emailInput);
                    form.appendChild(pwInput);
                    document.body.appendChild(form);
                    form.submit();

                    setTimeout(() => {
                        form.remove();
                        iframe.remove();
                    }, 2000);
                }
            }
        } else {
            setError(response.message || 'Nastala chyba. Skúste to znova neskôr.');
        }

        setLoading(false);
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Zmeniť heslo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {success ? (
                    <Alert variant="success" className="mb-0">
                        Heslo bolo úspešne zmenené.
                    </Alert>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Aktuálne heslo</label>
                            <div style={{position: 'relative'}}>
                                <input
                                    type={showCurrent ? 'text' : 'password'}
                                    className="form-control"
                                    style={{paddingRight: '2.5rem'}}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                    autoComplete="off"
                                    autoFocus
                                />
                                <span
                                    role="button"
                                    className="text-muted"
                                    tabIndex={-1}
                                    onClick={() => setShowCurrent(prev => !prev)}
                                    style={{position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', lineHeight: 0}}
                                >
                                    {showCurrent ? <IconEyeOff size={18}/> : <IconEye size={18}/>}
                                </span>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Nové heslo</label>
                            <div style={{position: 'relative'}}>
                                <input
                                    type={showNew ? 'text' : 'password'}
                                    className="form-control"
                                    style={{paddingRight: '2.5rem'}}
                                    placeholder="Minimálne 8 znakov"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    autoComplete="off"
                                />
                                <span
                                    role="button"
                                    className="text-muted"
                                    tabIndex={-1}
                                    onClick={() => setShowNew(prev => !prev)}
                                    style={{position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', lineHeight: 0}}
                                >
                                    {showNew ? <IconEyeOff size={18}/> : <IconEye size={18}/>}
                                </span>
                            </div>
                            {newPassword && (
                                <div className="mt-2">
                                    <ProgressBar
                                        now={strength.score}
                                        variant={strength.variant}
                                        style={{height: '6px'}}
                                    />
                                    <small className={`text-${strength.variant}`}>{strength.label}</small>
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Potvrdenie nového hesla</label>
                            <div style={{position: 'relative'}}>
                                <input
                                    type={showConfirm ? 'text' : 'password'}
                                    className="form-control"
                                    style={{paddingRight: '2.5rem'}}
                                    placeholder="Zopakujte nové heslo"
                                    value={newPasswordConfirm}
                                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                                    required
                                    minLength={8}
                                    autoComplete="off"
                                />
                                <span
                                    role="button"
                                    className="text-muted"
                                    tabIndex={-1}
                                    onClick={() => setShowConfirm(prev => !prev)}
                                    style={{position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', lineHeight: 0}}
                                >
                                    {showConfirm ? <IconEyeOff size={18}/> : <IconEye size={18}/>}
                                </span>
                            </div>
                        </div>

                        <div className="form-footer">
                            <button type="submit" className="btn btn-pill btn-primary w-100" disabled={loading}>
                                {loading ? 'Ukladám...' : 'Zmeniť heslo'}
                            </button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};
